import { useState } from "react";

// Downloads a file returned by the backend as a blob (e.g. server-generated .xlsx exports),
// as opposed to useCsvDownloadHook which builds a CSV client-side from data already in memory.
// fetchFn must be an async function that returns an axios response with responseType: 'blob'.
const useFileDownloadHook = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  const downloadFile = async (fetchFn, filename) => {
    setError(null);
    setIsDownloading(true);

    try {
      const response = await fetchFn();
      const contentType = response.headers?.["content-type"] || "application/octet-stream";

      // With responseType: 'blob', axios wraps whatever the server sent as a Blob regardless
      // of whether it's really a file - including a JSON error body (e.g. a validation failure
      // returned as `Ok(new ServiceResponse { Success = false, Message = ... })`). Without this
      // check that JSON text gets silently "downloaded" as if it were the real file.
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        let message = "Export failed.";
        try {
          message = JSON.parse(text)?.message || message;
        } catch {
          // Body wasn't valid JSON either - fall back to the generic message above.
        }
        throw new Error(message);
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, error, downloadFile };
};

export default useFileDownloadHook;
